import React, { useCallback, useMemo, useEffect } from 'react'

import { useSigEventsMap } from '../../sigevents/hooks'
import { BASE_URL, ICONS } from '../../constants'

import ClusterLayer from './ClusterLayer'

const getSigEventSymbol = (isSelected, getIncident) => (sigevent) => {
  const size = '50px'

  const iconPath = `${BASE_URL}${
    isSelected ? ICONS.AI.ACTIVE.DIR : ICONS.AI.DIR
  }`
  const incident = getIncident(sigevent.incidentId)
  const icon =
    (incident && incident.type && incident.type.icon) ||
    'SigEventIconsOther.png'

  return {
    type: 'picture-marker',
    url: `${iconPath}${icon}`,
    width: size,
    height: size,
  }
}

const getSigEventGeometry = (sigevent) => ({
  type: 'point',
  x: sigevent.longitude,
  y: sigevent.latitude,
})

const getSigEventAttributes = (sigevent) => ({
  id: sigevent.id,
})

const SigEventsLayer = ({ map, view }) => {
  const [
    { sigEvents, incidentsById, selected, editorIsOpen },
    { select, clear },
  ] = useSigEventsMap()

  const getIsSelected = useCallback(
    (graphic) =>
      selected.ids.length &&
      selected.ids[selected.current] === graphic.attributes.id,
    [selected]
  )

  const graphics = useMemo(
    () =>
      editorIsOpen
        ? []
        : sigEvents.map((sigevent) => {
            const isSelected =
              selected.ids.length &&
              selected.ids[selected.current] === sigevent.id
            return {
              geometry: getSigEventGeometry(sigevent),
              symbol: getSigEventSymbol(isSelected, incidentsById)(sigevent),
              attributes: getSigEventAttributes(sigevent),
            }
          }),
    [sigEvents, selected, incidentsById, editorIsOpen]
  )

  const onSelect = useCallback(
    (graphics) => {
      if (editorIsOpen) return
      if (
        !selected.ids.some((id) => graphics.some((g) => g.attributes.id === id))
      ) {
        select(graphics.map((g) => g.attributes.id))
      }
    },
    [select, selected, editorIsOpen]
  )

  const onDeselect = useCallback(() => {
    if (editorIsOpen) return
    clear()
  }, [editorIsOpen, clear])

  // Zoom/pan on selection change
  useEffect(() => {
    if (selected.ids.length) {
      const id = selected.ids[selected.current]
      const graphic = graphics.find((g) => g.attributes.id === id)
      if (
        graphic &&
        graphic.geometry &&
        graphic.geometry.x &&
        graphic.geometry.y
      ) {
        const zoom = view.zoom > 15 ? view.zoom : 15
        view.goTo({
          center: [graphic.geometry.x, graphic.geometry.y],
          zoom,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, view]) // only update when selection changes

  return (
    <ClusterLayer
      id="SigEventsLayer"
      map={map}
      view={view}
      graphics={graphics}
      getIsSelected={getIsSelected}
      onSelect={onSelect}
      onDeselect={onDeselect}
    />
  )
}

export default SigEventsLayer
